import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import { Badge, LRPill, StatusPill, TableCell, TableRow } from '@/components/ui';
import { leituraEmConferencia } from '@/lib/conferencia_da_leitura';
import { dataCivilEmUtc, eDataCivil } from '@/lib/data_civil';
import { CasosRowActions } from '@/components/Casos/CasosRowActions';
import { MarcaDaPreparacao } from '@/components/Casos/MarcaDaPreparacao';
import { marcaDoPrazo } from '@/components/Casos/ordemDaLista';
import { formatDeadline, situacaoNaLista } from '@/components/Casos/url_state';
import type { PreparacaoEmAndamentoData } from '@/lib/preparacoes';

const DATE_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});
// E176.F1.C01: a data civil (recebimento) não desliza para a véspera no fuso de quem opera.
const DATA_CIVIL_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  timeZone: 'UTC',
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ptBR.casos.placeholderEmDash;
  const civil = eDataCivil(iso);
  const d = civil ? dataCivilEmUtc(iso) : new Date(iso);
  if (Number.isNaN(d.getTime())) return ptBR.casos.placeholderEmDash;
  return (civil ? DATA_CIVIL_FMT : DATE_FMT).format(d);
}

interface CasosTableRowProps {
  caseRecord: CaseRecordData;
  alterandoPrioridade: boolean;
  baixandoRelatorio: boolean;
  /** E200.F2 (GUS-220): a preparação do pacote em curso ou com falha em aberto, na Situação. */
  preparacao?: PreparacaoEmAndamentoData | null;
  onOpen: () => void;
  onPreAnalise: () => void;
  onTogglePrioridade: () => void;
  onBaixarRelatorio: () => void;
}

/**
 * Uma linha da tabela de casos (extraída da `CasosPage` na E60.F3). // ux-neutrality:allow doc-comment
 *
 * A extração veio do limite de linhas da página, mas a divisão é a que faria // ux-neutrality:allow doc-comment
 * sentido de qualquer jeito: a página cuida de carregar, filtrar e paginar; a // ux-neutrality:allow doc-comment
 * linha cuida de exibir um caso. // ux-neutrality:allow doc-comment
 */
export function CasosTableRow({
  caseRecord: c,
  alterandoPrioridade,
  baixandoRelatorio,
  preparacao = null,
  onOpen,
  onPreAnalise,
  onTogglePrioridade,
  onBaixarRelatorio,
}: CasosTableRowProps): JSX.Element {
  const situacao = situacaoNaLista(c, preparacao !== null);
  const prazo = marcaDoPrazo(c, new Date());
  return (
    <TableRow
      data-testid="casos-row"
      data-display-code={c.display_code}
      className="cursor-pointer"
      onClick={onOpen}
    >
      <TableCell className="font-mono text-fg">
        {c.display_code}
        {/* E60.F3: sem a marca, a lista apareceria fora da ordem de chegada
            prometida e sem explicação. */}
        {c.prioritario === true && (
          <Badge
            variant="warning"
            className="ml-2 align-middle"
            data-testid={`casos-row-prioritario-${c.display_code}`}
          >
            {ptBR.casos.table.prioritarioBadge}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="text-fg">{c.title}</div>
        {c.requester !== null ? (
          <div className="text-caption text-fg-muted">{c.requester}</div>
        ) : null}
      </TableCell>
      <TableCell className="text-fg-muted">{c.especime ?? ptBR.casos.placeholderEmDash}</TableCell>
      <TableCell>
        <LRPill lr={c.latest_composite_lr ?? null} data-testid={`casos-row-lr-${c.display_code}`} />
      </TableCell>
      <TableCell>
        <StatusPill
          status={situacao.status}
          label={situacao.label}
          data-testid={`casos-row-status-${c.display_code}`}
        />
        {leituraEmConferencia(c.leitura_da_conferencia) ? (
          <Badge
            variant="warning"
            className="ml-2"
            data-testid={`casos-row-leitura-${c.display_code}`}
          >
            {ptBR.casos.leituraEmConferenciaMarca}
          </Badge>
        ) : null}
        {preparacao !== null ? (
          <MarcaDaPreparacao preparacao={preparacao} codigo={c.display_code} />
        ) : null}
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDeadline(c.deadline)}
        {/* owner, 03/11: o caso com prazo sobe na lista, e a marca diz por quê. */}
        {prazo !== null ? (
          <Badge
            variant={prazo.tom}
            className="ml-2 align-middle font-sans"
            data-testid={`casos-row-prazo-${c.display_code}`}
          >
            {prazo.texto}
          </Badge>
        ) : null}
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDate(c.received_at)}
      </TableCell>
      <TableCell className="font-mono text-caption text-fg-muted">
        {formatDate(c.created_at)}
      </TableCell>
      <TableCell
        className="text-right"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <CasosRowActions
          caseRecord={c}
          onOpen={onOpen}
          onPreAnalise={onPreAnalise}
          onTogglePrioridade={onTogglePrioridade}
          onBaixarRelatorio={onBaixarRelatorio}
          alterandoPrioridade={alterandoPrioridade}
          baixandoRelatorio={baixandoRelatorio}
        />
      </TableCell>
    </TableRow>
  );
}
